import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Button,
  Input,
  Checkbox,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss24Regular
} from '@fluentui/react-icons';

interface FilterDrawerData {
  columnKey: string;
  columnLabel: string;
  data: any[];
  currentFilters?: string[];
}

const useStyles = makeStyles({
  drawerBody: {
    ...shorthands.padding('0'),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  drawerHeader: {
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  searchSection: {
    ...shorthands.padding('16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  filterList: {
    flex: 1,
    overflowY: 'auto',
    ...shorthands.padding('8px', '0'),
  },
  filterItem: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding('8px', '16px'),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  filterLabel: {
    marginLeft: '8px',
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  filterCount: {
    marginLeft: 'auto',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    ...shorthands.padding('16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
  },
  emptyState: {
    ...shorthands.padding('32px', '16px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
  },
});

const DetailsDrawer: React.FC = () => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [filterData, setFilterData] = useState<FilterDrawerData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<Set<string>>(new Set());

  // Extract unique values from the column data
  const filterOptions = useMemo(() => {
    if (!filterData) return [];

    const valueCount = new Map<string, number>();

    filterData.data.forEach((item) => {
      const value = item[filterData.columnKey];
      const stringValue = value != null ? String(value) : '(Empty)';
      valueCount.set(stringValue, (valueCount.get(stringValue) || 0) + 1);
    });

    return Array.from(valueCount.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => {
        // Sort by count descending, then alphabetically
        if (b.count !== a.count) return b.count - a.count;
        return a.value.localeCompare(b.value);
      });
  }, [filterData]);

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return filterOptions;
    const query = searchQuery.toLowerCase();
    return filterOptions.filter(option =>
      option.value.toLowerCase().includes(query)
    );
  }, [filterOptions, searchQuery]);

  // Listen for filter drawer open events
  useEffect(() => {
    const handleOpenFilter = (event: CustomEvent<FilterDrawerData>) => {
      const data = event.detail;
      setFilterData(data);
      setIsOpen(true);
      setSearchQuery('');
      // Initialize with currently applied filters if provided
      setSelectedFilters(new Set(data.currentFilters || []));
    };

    window.addEventListener('openFilterDrawer', handleOpenFilter as EventListener);
    return () => {
      window.removeEventListener('openFilterDrawer', handleOpenFilter as EventListener);
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    setFilterData(null);
    setSearchQuery('');
    setSelectedFilters(new Set());
  };

  const handleToggleFilter = (value: string) => {
    const newSelected = new Set(selectedFilters);
    if (newSelected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    setSelectedFilters(newSelected);
  };

  const handleClearAll = () => {
    setSelectedFilters(new Set());
  };

  // Apply filters in real-time with debounce
  useEffect(() => {
    if (!filterData) return;

    const timeoutId = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('applyColumnFilter', {
        detail: {
          columnKey: filterData.columnKey,
          selectedValues: Array.from(selectedFilters),
        }
      }));
    }, 150); // 150ms debounce

    return () => clearTimeout(timeoutId);
  }, [selectedFilters, filterData]);

  return (
    <Drawer
      type="overlay"
      separator
      position="end"
      size="small"
      open={isOpen}
      onOpenChange={(_, { open }) => {
        if (!open) {
          handleClose();
        }
      }}
      modalType="non-modal"
    >
      <DrawerHeader className={styles.drawerHeader}>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={handleClose}
            />
          }
        >
          {filterData ? `Filter by ${filterData.columnLabel}` : 'Filter'}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        {/* Filter List */}
        <div className={styles.filterList}>
          {filteredOptions.length === 0 ? (
            <div className={styles.emptyState}>
              {searchQuery ? 'No matching values found' : 'No values available'}
            </div>
          ) : (
            <>
              {/* Individual Filter Options */}
              {filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={styles.filterItem}
                  onClick={() => handleToggleFilter(option.value)}
                >
                  <Checkbox
                    checked={selectedFilters.has(option.value)}
                  />
                  <span className={styles.filterLabel}>{option.value}</span>
                  <span className={styles.filterCount}>({option.count})</span>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button appearance="secondary" onClick={handleClearAll}>
            Clear All
          </Button>
        </div>
      </DrawerBody>
    </Drawer>
  );
};

export default DetailsDrawer;
